import { useState, useRef, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";

/**
 * Componente genérico de dropdown reutilizable con soporte de teclado
 * 
 * Proporciona un dropdown personalizado que puede ser usado para
 * cualquier tipo de filtro o selector con opciones.
 * 
 * Soporta navegación por teclado:
 * - Arrow Up/Down: Navegar entre opciones
 * - Enter: Seleccionar opción
 * - Escape: Cerrar dropdown
 * - Tab: Navegar fuera del dropdown
 * 
 * @component
 * @param {Array} options - Array de opciones con estructura { value, label }
 * @param {string} value - Valor actualmente seleccionado
 * @param {Function} onChange - Callback al cambiar la selección (recibe nuevo valor)
 * @param {string} [label] - Etiqueta opcional del dropdown
 * @param {string} [ariaLabel] - Etiqueta ARIA para accesibilidad
 * @param {string} [accentColor] - Color de acento para estilos (hex o CSS var)
 * @param {string} [containerClass] - Clase CSS personalizada para el contenedor
 * @param {string} [buttonClass] - Clase CSS personalizada para el botón
 * @param {string} [dropdownClass] - Clase CSS personalizada para el dropdown
 * @param {string} [optionClass] - Clase CSS personalizada para las opciones
 * 
 * @returns {JSX.Element} Dropdown con opciones
 */
function DropdownFilterComponent({
  options,
  value,
  onChange,
  label,
  ariaLabel = "Seleccionar opción",
  accentColor,
  containerClass,
  buttonClass,
  dropdownClass,
  optionClass
}, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Exponer método para cerrar el dropdown desde el padre
  useImperativeHandle(ref, () => ({
    closeDropdown: () => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  }), []);

  // Obtener el label de la opción seleccionada
  const selectedLabel = useMemo(() => {
    const option = options.find(opt => opt.value === value);
    return option?.label || value;
  }, [value, options]);

  // Obtener índice de la opción seleccionada
  const selectedIndex = useMemo(() => {
    return options.findIndex(opt => opt.value === value);
  }, [value, options]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    // Solo agregar listener si el dropdown está abierto
    if (isOpen) {
      document.addEventListener("click", handleClickOutside, false);
      return () => {
        document.removeEventListener("click", handleClickOutside, false);
      };
    }
  }, [isOpen]);

  // Manejar selección de opción
  const handleOptionSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setHighlightedIndex(-1);
    buttonRef.current?.focus();
  };

  // Manejar eventos de teclado
  const handleKeyDown = (event) => {
    if (!isOpen && (event.key === "Enter" || event.key === " " || event.key === "ArrowDown")) {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
      return;
    }

    if (!isOpen) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex(prev => 
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;

      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : 0
        );
        break;

      case "Enter":
        event.preventDefault();
        if (highlightedIndex >= 0) {
          handleOptionSelect(options[highlightedIndex].value);
        }
        break;

      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        buttonRef.current?.focus();
        break;

      case "Tab":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;

      default:
        break;
    }
  };

  // Manejar eventos de teclado en opciones
  const handleOptionKeyDown = (event, index) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (index < options.length - 1) {
          setHighlightedIndex(index + 1);
        }
        break;

      case "ArrowUp":
        event.preventDefault();
        if (index > 0) {
          setHighlightedIndex(index - 1);
        }
        break;

      case "Enter":
      case " ":
        event.preventDefault();
        handleOptionSelect(options[index].value);
        break;

      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        buttonRef.current?.focus();
        break;

      case "Tab":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;

      default:
        break;
    }
  };

  return (
    <div className={containerClass} ref={dropdownRef}>
      {label && <label className={`${containerClass}__label`}>{label}</label>}
      
      <button
        ref={buttonRef}
        className={buttonClass}
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
          } else {
            setHighlightedIndex(-1);
          }
        }}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={`${buttonClass}__text`}>{selectedLabel.toUpperCase()}</span>
        <span className={`${buttonClass}__arrow`}>▼</span>
      </button>

      {isOpen && (
        <div className={dropdownClass} role="listbox" aria-label={ariaLabel}>
          {options.map((option, index) => (
            <div
              key={option.value}
              className={`${optionClass} ${value === option.value ? "active" : ""} ${highlightedIndex === index ? "highlighted" : ""}`}
              onClick={() => handleOptionSelect(option.value)}
              onKeyDown={(e) => handleOptionKeyDown(e, index)}
              onMouseEnter={() => setHighlightedIndex(index)}
              onMouseLeave={() => setHighlightedIndex(-1)}
              role="option"
              aria-selected={value === option.value}
              tabIndex={highlightedIndex === index ? 0 : -1}
              style={value === option.value && accentColor ? { background: `${accentColor}20` } : undefined}
            >
              <span className={`${optionClass}__name`}>{option.label.toUpperCase()}</span>
              {value === option.value && (
                <span className={`${optionClass}__check`} style={accentColor ? { color: accentColor } : undefined}>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const DropdownFilter = forwardRef(DropdownFilterComponent);
DropdownFilter.displayName = "DropdownFilter";

export default DropdownFilter;